/**
 * SEARCH-002 §4.6/4.7 + §A6 — Panneau d'aperçu de la palette ⌘K (desktop :
 * colonne droite ~44 % · mobile : bottom-sheet).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CE PANNEAU NE DESSINE PLUS RIEN LUI-MÊME (2026-08-11)
 * ═══════════════════════════════════════════════════════════════════════════
 * Il rendait sa propre présentation : une liste plate `fields[]`, sans section,
 * sans icône de ligne, valeurs non mises en exergue. Le PO a comparé les quatre
 * surfaces (résultats ⌘K, aperçu ⌘K, volet de synthèse, carte de survol) et
 * tranché : c'est la **carte** qui se lit le mieux, et les deux autres doivent
 * s'en rapprocher.
 *
 * Une liste plate ne peut structurellement pas produire de sections titrées. Ce
 * panneau consomme donc désormais le contrat riche `SummaryDetail` et délègue
 * TOUT le rendu à `SummaryContent` — le composant du volet, lui-même aligné sur
 * la carte. Une seule présentation à maintenir, donc une seule à faire dériver.
 *
 * Il ne reste ici que ce qui est propre à la palette : la région annoncée, le
 * squelette, l'état vide, et le passage du « coup d'œil » de la pièce (dont le
 * lecteur plein écran est monté par `GlobalSearchDialog`, hors de ce panneau
 * pour survivre à sa fermeture).
 */

import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { SummaryContent } from '@/components/summary/SummaryContent'
import { BTN_SECONDARY, SummarySkeleton } from '@/components/summary/primitives'
import type { SummaryDetail } from '@/components/summary/types'
import { cn } from '@/lib/utils'
import type { QuickLookTarget } from './searchPaletteHelpers'

interface SearchPreviewPanelProps {
    data: SummaryDetail | undefined
    isLoading: boolean
    /**
     * ⚠️ Sans ça, un chargement en ÉCHEC affichait « Sélectionnez un résultat
     * pour l'aperçu » alors qu'un résultat ÉTAIT sélectionné (constat PO
     * 2026-08-11). Le message accusait l'utilisateur de ne pas avoir cliqué, et
     * cachait la seule information utile : la fiche n'a pas pu être chargée.
     */
    isError?: boolean
    onRetry?: () => void
    /** Ouvre la fiche du hit sélectionné (l'appelant connaît la route). */
    onOpen: () => void
    /** Navigation in-app depuis une action secondaire (« Voir le client »…). */
    onNavigate: (href: string) => void
    /** Ouvre le lecteur plein écran « coup d'œil » (§6.2) sur la version courante. */
    onQuickLook: (target: QuickLookTarget) => void
    /** Entité courante (left menu) — le badge d'entité n'apparaît que si ≠. */
    currentEntityId?: string | null
    /** Compositeur d'email in-app tracé — ouvert à tous (décision PO, ungated). */
    onSendEmail?: (target: { email: string; name: string }) => void
    className?: string
}

export function SearchPreviewPanel({
    data,
    isLoading,
    isError = false,
    onRetry,
    onOpen,
    onNavigate,
    onQuickLook,
    currentEntityId,
    onSendEmail,
    className,
}: SearchPreviewPanelProps) {
    const { t } = useTranslation()

    return (
        <div
            role="region"
            aria-label={t('globalSearch.preview.title')}
            aria-live="polite"
            className={cn('text-sm', className)}
        >
            {/* Pas d'étiquette « Aperçu » visible : le rôle de cette colonne se
                comprend naturellement (elle reflète la ligne sélectionnée). On
                garde l'`aria-label` de la région pour les lecteurs d'écran, et on
                récupère la surface pour les informations. */}
            {isLoading && !data ? (
                <SummarySkeleton />
            ) : isError && !data ? (
                <div className="flex flex-col items-start gap-3 p-5 text-xs text-muted-foreground">
                    <p>{t('globalSearch.preview.error', 'Impossible de charger cette fiche.')}</p>
                    {onRetry && (
                        <button type="button" onClick={onRetry} className={BTN_SECONDARY}>
                            {t('common.retry', 'Réessayer')}
                        </button>
                    )}
                </div>
            ) : !data ? (
                <p className="p-5 text-muted-foreground">{t('globalSearch.preview.empty')}</p>
            ) : (
                <SummaryContent
                    data={data}
                    currentEntityId={currentEntityId ?? null}
                    onOpen={onOpen}
                    onNavigate={onNavigate}
                    onSendEmail={onSendEmail ?? (() => {})}
                    // ⚠️ Pas de bloc « Santé entreprise » dans la palette : il
                    // déclenche un fetch de détail client et tire le bundle
                    // santé financière. Flécher sur cinq clients en
                    // déclencherait cinq, et l'aperçu ne doit pas dégrader le
                    // SLO du listing (§6.10). Le volet, lui, l'affiche : on y
                    // arrive par un geste délibéré.
                    showClientHealth={false}
                    onQuickLook={(fileId) =>
                        onQuickLook({
                            fileId,
                            filename: data.header.label,
                            mime: data.document?.mime ?? null,
                            // Entité PROPRIÉTAIRE du fichier, pas l'entité courante :
                            // en cross-entités, l'en-tête GED de l'URL signée doit
                            // désigner la bonne, sinon le lecteur rend « Aperçu
                            // indisponible » sur un document d'une autre entité.
                            entityId: data.entity?.id ?? null,
                        })
                    }
                />
            )}

            {isLoading && data && (
                <div className="flex items-center gap-2 px-5 pb-4 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    {t('globalSearch.preview.loading')}
                </div>
            )}
        </div>
    )
}
